import React, { useContext, useEffect, useRef, useState } from 'react'
import { Text, View } from 'oio-react'
import { useParams } from 'react-router-dom'
import { LAYOUT_BOX, LAYOUT_HEADER } from 'config/constants/ui'
import Title from 'src/apps/CustomApp/components/Title'
import { useMessageList, useMyDirectThreadList, useMyNotificationCount,
   useThread } from 'src/graphql/hooks'
import ProtoThemeContext from 'src/libraries/contexts/ProtoTheme'
import MessagesContext from '~/components/Context'
import MessageInput from '~/components/Input'
import ChatMessageListItem from '~/components/ChatMessageListItem'
import UserThreadMoreControl from '~/components/UserThreadMoreControl'

const ThreadChatView = () => {
   const messageScrollContainer = useRef()
   const { threadId } = useParams()
   const { protoTheme: theme } = useContext(ProtoThemeContext)
   const [visible, setVisible] = useState(false)

   const { refetch: refetchNotificationCount } = useMyNotificationCount()
   const { refetch: refetchMyDirectThreadList } = useMyDirectThreadList()
   const isBoxGL = theme.tmpGlobalLayout === LAYOUT_BOX
   const isHeaderGL = theme.tmpGlobalLayout === LAYOUT_HEADER

   const { thread, networkStatus } = useThread({ id: threadId })
   const initialLoading = networkStatus <= 2

   const isUserThread = thread?.__typename === 'UserThread'
   const threadTitle = isUserThread ? thread?.title : 'Messages'
   const threadTargetId = isUserThread ? thread?.recipientUserId : thread?.initiative.id
   const threadTargetType = isUserThread ? 'user' : 'initiative'
   const currentUserCanModerate = isUserThread ? false : thread?.initiative.currentUserCanModerate

   const contentPaddingLeft = `
      ${theme.tmpViewportPaddingA}[a]
      ${theme.tmpViewportPaddingB}[b]
      30px[c-d]
      40px[e-f]`

   const contentPaddingRight = (isHeaderGL || isBoxGL) && `
      ${theme.tmpViewportPaddingC}[c]
      ${theme.tmpViewportPaddingD}[d]
      ${theme.tmpViewportPaddingE}[e]
      ${theme.tmpViewportPaddingF}[f]
   `

   const { messageList, loading, networkStatus: messageListNetworkStatus, fetchMore } =
      useMessageList({
         limit: 20,
         sortBy: 'dateDesc',
         type: 'human',
         threadId
      })

   // Reverse order of messages to show latest at the bottom
   const messageListItems = [...messageList.items].reverse()
   const messageListInitialLoading = messageListNetworkStatus <= 2

   const handleLoadMore = () => {
      fetchMore({
         variables: {
            start: messageList.listInfo.nextCursor
         },
         updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) {
               return prevResult
            }

            return {
               ...prevResult,
               messageList: {
                  ...prevResult.messageList,
                  items: [
                     ...prevResult.messageList.items,
                     ...fetchMoreResult.messageList.items
                  ],
                  listInfo: fetchMoreResult.messageList.listInfo
               }
            }
         }
      })
   }

   const scrollToBottom = () => {
      messageScrollContainer.current.scrollTop = messageScrollContainer.current.scrollHeight
   }

   useEffect(() => {
      // TODO: Figure out the setTimeout is necessary
      // See PR #614
      if (messageScrollContainer.current) {
         setTimeout(() => {
            scrollToBottom()
            setVisible(true)
         }, 10)
      }
   }, [messageList])

   // Refetch Notification Count when first viewing thread
   // Refetch MyDirectThreadList to update badge in Message sidebar
   useEffect(() => {
      refetchNotificationCount()
      if (isUserThread) {
         refetchMyDirectThreadList()
      }
   }, [isUserThread, threadId, messageListInitialLoading])

   if (messageListInitialLoading) {
      return null
   }

   return (
      <MessagesContext.Provider popoverAnchorOriginVertical="bottom">
         <View
            display="flex"
            flexFlow="column"
            position="relative"
            width="100%"
            height="100%">
            <View
               flex="0 0 auto"
               paddingRight={contentPaddingRight}>
               <View
                  backgroundColor={theme.tmpContentBackgroundColor}
                  paddingHorizontal={contentPaddingLeft}>
                  <View
                     display="flex"
                     justifyContent="space-between"
                     width="100%"
                     paddingVertical={theme.tmpSpacingTop}
                     borderBottom={`1px solid ${theme.tmpContentLineColor}`}>
                     <Title size="md" color={theme.tmpContentTitleColor}>{threadTitle}</Title>
                     {isUserThread && <UserThreadMoreControl threadId={threadId} />}
                  </View>
               </View>
            </View>
            <View
               ref={messageScrollContainer}
               position="relative"
               flex="1 1 auto"
               paddingRight={contentPaddingRight}
               scroll="on">
               <View
                  position="relative"
                  display="flex"
                  flexFlow="column"
                  justifyContent="flex-end"
                  width="100%"
                  minHeight="100vh"
                  paddingVertical="20px"
                  paddingHorizontal={contentPaddingLeft}
                  backgroundColor={theme.tmpContentBackgroundColor}>
                  {messageList.listInfo.hasNext && (
                     <View
                        onClick={handleLoadMore}
                        width="100%"
                        borderBottom="1px solid #eee"
                        padding="20px 0px"
                        transition="300ms"
                        opacity={visible ? 1 : 0}
                        style={{ cursor: 'pointer' }}>
                        <Text size="1.5" weight="medium" color="#888">
                           {loading ? 'Loading...' : 'View earlier messages...'}
                        </Text>
                     </View>
                  )}
                  {messageListItems.map((message, index) => (
                     <View
                        key={message.id}
                        transition="300ms"
                        opacity={visible ? 1 : 0}>
                        <ChatMessageListItem
                           id={message.id}
                           allowReplyCreate
                           anon={message.anon}
                           authorAvatarUrl={message?.author?.avatarMedia?.file.thumbnailUrlW48}
                           authorId={message?.author?.id}
                           authorName={message?.author?.fullName}
                           body={message.body}
                           borderStyle={index === 0
                              ? 'none'
                              : `1px solid ${theme.tmpContentLineColor}`
                           }
                           currentUserCanModerate={currentUserCanModerate}
                           edited={message.edited}
                           dateAdded={message.dateAdded}
                           isLikedByMe={message.isLikedByMe}
                           isMine={message.isMine}
                           numLikes={message.numLikes}
                           replies={message.replies}
                           textColor={theme.tmpBodyTextColor}
                           textSize={theme.tmpBodyTextSize}
                           textLineHeight={theme.tmpBodyTextLineHeight}
                        />
                     </View>
                  ))}
               </View>
            </View>
            <View flex="0 0 auto" paddingRight={contentPaddingRight}>
               <View
                  float="left"
                  width="100%"
                  paddingTop="10px"
                  paddingBottom="20px"
                  paddingHorizontal={contentPaddingLeft}
                  backgroundColor={theme.tmpContentBackgroundColor}>
                  {!initialLoading && (
                     <MessageInput
                        allowFileAttachments
                        inputMinHeight="120px"
                        inputToolbarConfig="chat"
                        onInputFocus={() => setTimeout(scrollToBottom, 100)}
                        onSubmit={scrollToBottom}
                        placeholderText="Message"
                        submitButtonName="Send"
                        threadTargetId={threadTargetId}
                        threadTargetType={threadTargetType}
                     />
                  )}
               </View>
            </View>
         </View>
      </MessagesContext.Provider>
   )
}

export default ThreadChatView
